import React from 'react';
import round from 'lodash/round';
import TableFavoriteButton, {
  Props as TableFavoriteButtonProps,
} from '~components/TableFavoriteButton';
import { ObjectType } from '~entities/Object';
import {
  CustomItemConfig,
  TableConfig,
  TextItemConfig,
} from '~components/Table';
import getFormattedPrice from '~utils/getFormattedPrice';
import { CurrencyType } from '~constants/currencyMap';

type Options = {
  favoriteButtonProps?: Pick<
    TableFavoriteButtonProps,
    'hasFavorites' | 'toggleFavorite'
  >;
  currency?: CurrencyType;
  typeListing?: ObjectType['type_listing'];
  hasIdOnDesktop?: boolean;
};

type TableConfigForObject<T extends ObjectType> = TableConfig<T>;
type TextConfigForObject<T extends ObjectType> = TextItemConfig<T>;
type CustomConfigForObject<T extends ObjectType> = CustomItemConfig<T>;

type ReturnType<T extends ObjectType> = {
  mobileConfig: TableConfigForObject<T>;
  tabletConfig: TableConfigForObject<T>;
  desktopConfig: TableConfigForObject<T>;
};

export default function<T extends ObjectType>({
  currency = 'ruble',
  typeListing,
  favoriteButtonProps,
  hasIdOnDesktop = false,
}: Options = {}): ReturnType<T> {
  const isDollar = currency === 'dollar';
  const costmField = isDollar ? 'costm_d' : 'costm_r';
  const costField = isDollar ? 'cost_d' : 'cost_r';

  const idConfig: TextConfigForObject<T> = {
    field: 'id_crm',
    type: 'text',
    getThLabel: () => 'ID',
  };

  const nameTableConfig: TextConfigForObject<T> = {
    field: 'name',
    type: 'text',
    isOpener: true,
    getThLabel: () => 'Башня',
  };

  const costTableConfig: TextConfigForObject<T> = {
    field: costField,
    type: 'text',
    isSortable: true,
    getThLabel: () => (typeListing === 'rent' ? 'В месяц' : 'Стоимость'),
    getTdLabel: ({ data: { cost_r, cost_d } }) =>
      getFormattedPrice({ price: isDollar ? cost_d : cost_r, currency }),
  };

  const floorTableConfig: TextConfigForObject<T> = {
    field: 'floor',
    type: 'text',
    isSortable: true,
    getThLabel: () => 'Этаж',
  };

  const totalAreaConfig: TextConfigForObject<T> = {
    field: 'total_area',
    type: 'text',
    isSortable: true,
    getThLabel: () => 'Площадь',
    getTdLabel: ({ data: { total_area } }) => `${round(+total_area)} м²`,
  };

  const costmTableConfig: TextConfigForObject<T> = {
    field: costmField,
    type: 'text',
    isSortable: true,
    getThLabel: () => 'Цена за кв.м',
    getTdLabel: ({ data: { costm_r, costm_d } }) =>
      getFormattedPrice({ price: isDollar ? costm_d : costm_r, currency }),
  };

  const desktopConfig: TableConfigForObject<T> = [
    nameTableConfig,
    floorTableConfig,
    totalAreaConfig,
    costTableConfig,
    costmTableConfig,
  ];

  if (hasIdOnDesktop) {
    desktopConfig.push(idConfig);
  }

  if (favoriteButtonProps) {
    const favoritesButtonConfig: CustomConfigForObject<T> = {
      type: 'custom',
      render: ({ id_crm }) => (
        <TableFavoriteButton id={id_crm} {...favoriteButtonProps} />
      ),
    };
    desktopConfig.push(favoritesButtonConfig);
  }

  const tabletConfig = desktopConfig.filter((item) => {
    if (item.type === 'text') {
      return (
        item.field !== nameTableConfig.field && item.field !== idConfig.field
      );
    }

    return item.type !== 'custom';
  });

  const mobileConfig = tabletConfig.filter((item) => {
    if (item.type === 'text') {
      return item.field !== costmTableConfig.field;
    }

    return false;
  });

  return {
    mobileConfig,
    tabletConfig,
    desktopConfig,
  };
}
